import React from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import {
  WHEEL_POSITIONS,
  WHEEL_INSURANCE_OPTIONS,
  WHEEL_TYPE_OPTIONS,
  WHEEL_RESTORATION_METHOD_OPTIONS,
  WHEEL_CLEAR_COAT_OPTIONS,
  WHEEL_PRECISION_METHOD_OPTIONS,
  WHEEL_YES_NO_OPTIONS,
  WHEEL_GOOD_BAD_OPTIONS,
  WHEEL_CONDITION_OPTIONS,
  WHEEL_LOCATION_ITEMS,
  KCWA_CUSTOMER_CENTER_PHONE,
  KCWA_ORGANIZATION_NAME,
  formatWheelLocationItemTitle,
} from '../../constants/WheelInspectionConstants';
import {
  parseWheelInspection,
  getPositionLabel,
} from '../../utils/wheelInspectionUtil';
import {
  parseWheelInformation,
  formatWheelSpecRow,
} from '../../utils/wheelInformationUtil';
import { WheelReportCheck, WheelReportCheckGroup } from './WheelReportCheck';
import WheelRestorationPrePhotoSection from './WheelRestorationPrePhotoSection';
import kcwaSeal from '../../assets/images/kcwa_seal.png';
import './WheelRestorationReport.scss';

const SAFETY_DIAGNOSIS_ROWS = [
  {
    key: 'crack',
    title: '크렉 (Crack)',
    desc: '휠 전반의 균열 유무 (기준 2cm 이하)',
    options: WHEEL_YES_NO_OPTIONS,
    path: 'safety.crack',
    positionsPath: 'safety.crackPositions',
  },
  {
    key: 'hairline',
    title: '실금 (Hairline)',
    desc: '표면 미세 실금 유무',
    options: WHEEL_YES_NO_OPTIONS,
    path: 'safety.hairline',
    positionsPath: 'safety.hairlinePositions',
  },
  {
    key: 'paintState',
    title: '도막 상태',
    desc: '도막(Painting) 및 코팅 전체 상태',
    options: WHEEL_GOOD_BAD_OPTIONS,
    path: 'safety.paintState',
    positionsPath: 'safety.paintStatePositions',
  },
  {
    key: 'roadForce',
    title: '컷팅 부분',
    desc: '휠 다이아몬드 컷팅 부위',
    options: WHEEL_GOOD_BAD_OPTIONS,
    path: 'safety.roadForce',
    positionsPath: 'safety.roadForcePositions',
  },
  {
    key: 'scratch',
    title: '스크래치',
    desc: '림 주변 및 스포크 표면 스크래치 상태',
    options: WHEEL_YES_NO_OPTIONS,
    path: 'safety.scratch',
    positionsPath: 'safety.scratchPositions',
  },
];

const LOCATION_DETAIL_ROWS = WHEEL_LOCATION_ITEMS;

const displayValue = (value) =>
  value !== undefined && value !== null && String(value).trim() !== ''
    ? String(value)
    : '';

const WheelRestorationReport = ({
  data = {},
  vehicleInformation = {},
  customerCompany = {},
  photos = [],
  inspector = {},
  part = 'all',
}) => {
  const inspection = parseWheelInspection(vehicleInformation);
  const wheel = parseWheelInformation(vehicleInformation, data);
  const front = formatWheelSpecRow('front', wheel);
  const rear = formatWheelSpecRow('rear', wheel);

  const checkDate = _.get(data, 'CHCK_DE');
  const formattedDate = checkDate
    ? dayjs(checkDate, 'YYYYMMDD')
    : dayjs();
  const printDate = dayjs();

  const selectedPositions = _.get(inspection, 'preRestoration.positions', []);

  const headerBlock = (
    <div className="wheel-report-header">
      <h1 className="wheel-report-title">휠 안전 검사 및 복원 기록부</h1>
      <div className="wheel-report-meta">
        <span>
          접수번호: {displayValue(_.get(data, 'PRFOMNC_CHCK_NO')) || '—'}
        </span>
        <span>
          검사일자: {formattedDate.format('YYYY')}년{' '}
          {formattedDate.format('MM')}월 {formattedDate.format('DD')}일
        </span>
      </div>
    </div>
  );

  const infoSections = (
    <>
      <h2 className="wheel-report-section-title">1. 고객 정보</h2>
      <table className="wheel-report-table">
        <colgroup>
          <col style={{ width: '15%' }} />
          <col style={{ width: '35%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '35%' }} />
        </colgroup>
        <tbody>
          <tr>
            <th>고객명</th>
            <td className="wheel-report-value">
              {displayValue(
                _.get(data, 'NTIC_PERSON_NM') ||
                  _.get(customerCompany, 'companyName')
              ) || '—'}
            </td>
            <th>연락처</th>
            <td className="wheel-report-value">
              {displayValue(_.get(customerCompany, 'mainPhone')) || '—'}
            </td>
          </tr>
          <tr>
            <th>회원번호</th>
            <td colSpan={3} className="wheel-report-value">
              {displayValue(
                _.get(customerCompany, 'businessRegistrationNumber')
              ) || '—'}
            </td>
          </tr>
        </tbody>
      </table>

      <h2 className="wheel-report-section-title">2. 차량 정보</h2>
      <table className="wheel-report-table">
        <colgroup>
          <col style={{ width: '15%' }} />
          <col style={{ width: '35%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '35%' }} />
        </colgroup>
        <tbody>
          <tr>
            <th>차량명</th>
            <td className="wheel-report-value">{_.get(data, 'CNM') || '—'}</td>
            <th>차량번호</th>
            <td className="wheel-report-value">{_.get(data, 'VHRNO') || '—'}</td>
          </tr>
          <tr>
            <th>주행거리</th>
            <td colSpan={3} className="wheel-report-value">
              {displayValue(_.get(data, 'TRVL_DSTNC'))
                ? `${Number(_.get(data, 'TRVL_DSTNC')).toLocaleString()} km`
                : '—'}
            </td>
          </tr>
        </tbody>
      </table>

      <h2 className="wheel-report-section-title">3. 휠 기본 정보</h2>
      <table className="wheel-report-table">
        <colgroup>
          <col style={{ width: '15%' }} />
          <col style={{ width: '35%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '35%' }} />
        </colgroup>
        <tbody>
          <tr>
            <th>제품명</th>
            <td className="wheel-report-value">{wheel.productName || '—'}</td>
            <th>품번 (P/N)</th>
            <td className="wheel-report-value">{wheel.partNumber || '—'}</td>
          </tr>
          <tr>
            <th>사이즈</th>
            <td colSpan={3} className="wheel-report-value">
              {wheel.size ? `${wheel.size} 인치` : '—'}
            </td>
          </tr>
        </tbody>
      </table>
      <table className="wheel-report-table" style={{ marginTop: -4 }}>
        <colgroup>
          <col style={{ width: '15%' }} />
          <col style={{ width: '17%' }} />
          <col style={{ width: '17%' }} />
          <col style={{ width: '17%' }} />
          <col style={{ width: '17%' }} />
          <col style={{ width: '17%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>구분 항목</th>
            <th>림폭 (Rim)</th>
            <th>옵셋 (Offset)</th>
            <th>홀수 (Hole)</th>
            <th>P.C.D</th>
            <th>허브 (Hub)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th>프론트휠 제원</th>
            {front.map((val, idx) => (
              <td key={`f_${idx}`} className="wheel-report-value text-center">
                {val}
              </td>
            ))}
          </tr>
          <tr>
            <th>리어휠 제원</th>
            {rear.map((val, idx) => (
              <td key={`r_${idx}`} className="wheel-report-value text-center">
                {val}
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      <WheelRestorationPrePhotoSection
        photos={photos}
        vehicleInformation={vehicleInformation}
      />
    </>
  );

  const inspectionSections = (
    <>
      <h2 className="wheel-report-section-title">
        5. 휠 복원전 검사 항목 (기초진단)
      </h2>
      <table className="wheel-report-table">
        <colgroup>
          <col style={{ width: '15%' }} />
          <col style={{ width: '85%' }} />
        </colgroup>
        <tbody>
          <tr>
            <th>보험처리 여부</th>
            <td>
              <WheelReportCheckGroup
                options={WHEEL_INSURANCE_OPTIONS}
                value={_.get(inspection, 'preRestoration.insuranceHandled')}
              />
            </td>
          </tr>
          <tr>
            <th>5-1 위치</th>
            <td>
              {WHEEL_POSITIONS.map((p) => (
                <WheelReportCheck
                  key={p.code}
                  label={p.label.replace(/ \(.*\)/, '')}
                  checked={selectedPositions.includes(p.code)}
                />
              ))}
            </td>
          </tr>
          <tr>
            <th>5-2 휠 종류</th>
            <td>
              {WHEEL_TYPE_OPTIONS.map((opt) => (
                <WheelReportCheck
                  key={opt.value}
                  label={opt.label}
                  checked={(_.get(inspection, 'safety.wheelTypes') || []).includes(
                    opt.value
                  )}
                />
              ))}
            </td>
          </tr>
          <tr>
            <th>5-3 복원 방법</th>
            <td>
              {WHEEL_RESTORATION_METHOD_OPTIONS.map((opt) => (
                <WheelReportCheck
                  key={opt.value}
                  label={opt.label}
                  checked={(
                    _.get(inspection, 'safety.restorationMethods') || []
                  ).includes(opt.value)}
                />
              ))}
            </td>
          </tr>
          <tr>
            <th>5-4 크리어 코트</th>
            <td>
              <WheelReportCheckGroup
                options={WHEEL_CLEAR_COAT_OPTIONS}
                value={_.get(inspection, 'safety.clearCoatType')}
              />
            </td>
          </tr>
        </tbody>
      </table>

      <table className="wheel-report-table" style={{ marginTop: -4 }}>
        <colgroup>
          <col style={{ width: '15%' }} />
          <col style={{ width: '37%' }} />
          <col style={{ width: '20%' }} />
          <col style={{ width: '28%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>구분</th>
            <th>세부 검사항목 및 기준</th>
            <th>결과 (체크)</th>
            <th>정밀 검사 방법 (해당 항목 체크)</th>
          </tr>
        </thead>
        <tbody>
          {SAFETY_DIAGNOSIS_ROWS.map((row, rowIdx) => (
            <tr key={row.key}>
              <th>{row.title}</th>
              <td>{row.desc}</td>
              <td className="wheel-report-cell-checks">
                {row.positionsPath ? (
                  <div className="wheel-report-result-split">
                    <div className="wheel-report-result-split__positions">
                      {WHEEL_POSITIONS.map((p) => (
                        <span key={p.code} className="wheel-report-result-split__position-row">
                          <WheelReportCheck
                            label={getPositionLabel(p.code)}
                            checked={(
                              _.get(inspection, row.positionsPath) || []
                            ).includes(p.code)}
                          />
                        </span>
                      ))}
                    </div>
                    <div className="wheel-report-result-split__value">
                      <WheelReportCheckGroup
                        options={row.options}
                        value={_.get(inspection, row.path)}
                      />
                    </div>
                  </div>
                ) : (
                  <WheelReportCheckGroup
                    options={row.options}
                    value={_.get(inspection, row.path)}
                  />
                )}
              </td>
              {rowIdx === 0 && (
                <td rowSpan={SAFETY_DIAGNOSIS_ROWS.length} style={{ lineHeight: 1.7 }}>
                  {WHEEL_PRECISION_METHOD_OPTIONS.map((opt) => (
                    <span key={opt.value} style={{ display: 'block' }}>
                      <WheelReportCheck
                        label={opt.label}
                        checked={(
                          _.get(inspection, 'safety.precisionMethods') || []
                        ).includes(opt.value)}
                      />
                    </span>
                  ))}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      <h2 className="wheel-report-section-title wheel-report-section-title--section-gap">
        6. 복원후 위치별 상세점검 및 측정(안전검사)
      </h2>
      <table className="wheel-report-table">
        <colgroup>
          <col style={{ width: '20%' }} />
          {WHEEL_POSITIONS.map((p) => (
            <col key={p.code} style={{ width: '20%' }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th>위치별 검사항목</th>
            {WHEEL_POSITIONS.map((p) => (
              <th key={p.code}>{getPositionLabel(p.code)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {LOCATION_DETAIL_ROWS.map((row) => (
            <tr key={row.key}>
              <th>{formatWheelLocationItemTitle(row)}</th>
              {WHEEL_POSITIONS.map((p) => (
                <td key={`${row.key}_${p.code}`} className="wheel-report-cell-checks">
                  <WheelReportCheckGroup
                    options={row.options}
                    value={_.get(
                      inspection,
                      `locationDetail.${p.code}.${row.key}`
                    )}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ fontWeight: 'bold', fontSize: '8.5pt', margin: '8px 0 4px' }}>
        ※ 특이사항 및 종합의견
      </div>
      <div className="wheel-report-note-box">
        {displayValue(_.get(inspection, 'safety.specialNote')) || '—'}
      </div>

      <div className="wheel-report-terms">
        본 검사지는 {KCWA_ORGANIZATION_NAME}의 정밀 안전 검사 기준에 의거하여
        육안 및 전문 측정 장비로 진단된 결과 기록부입니다. 휠의 균열 및 변형은
        주행 안전성에 직접적인 영향을 미치므로 주기적인 점검을 권장합니다.
      </div>

      <div className="wheel-report-footer">
        <div className="wheel-report-footer-date">
          {printDate.format('YYYY')}년 {printDate.format('MM')}월{' '}
          {printDate.format('DD')}일
        </div>
        <div className="wheel-report-footer-sign">
          <div className="wheel-report-footer-sign-line wheel-report-footer-org-row">
            <span className="wheel-report-footer-org">
              {KCWA_ORGANIZATION_NAME}{' '}
              <span className="wheel-report-footer-sign-stamp">
                (인)
                <img
                  src={kcwaSeal}
                  alt=""
                  className="wheel-report-footer-seal"
                  aria-hidden
                />
              </span>
            </span>
            <span className="wheel-report-footer-customer-center">
              고객센터 {KCWA_CUSTOMER_CENTER_PHONE}
            </span>
          </div>
          <div className="wheel-report-footer-sign-line">
            휠 / 타이어관리사
            {displayValue(_.get(inspector, 'userName')) && (
              <span className="wheel-report-footer-sign-name">
                {displayValue(_.get(inspector, 'userName'))}
              </span>
            )}
            (인)
          </div>
        </div>
      </div>
    </>
  );

  let content = null;
  if (part === 'info') {
    content = (
      <>
        {headerBlock}
        {infoSections}
      </>
    );
  } else if (part === 'inspection') {
    content = inspectionSections;
  } else {
    content = (
      <>
        {headerBlock}
        {infoSections}
        {inspectionSections}
      </>
    );
  }

  return <div className={`wheel-restoration-report${part === 'info' ? ' wheel-restoration-report--info' : ''}`}>{content}</div>;
};

export default WheelRestorationReport;
